<template>
	<div class="coordinate-picker">
		<v-layout row wrap>
			<v-flex xs12>
				<v-card>
					<v-card-title>
						<span class="headline">Locatie selecteren</span>
						<v-spacer></v-spacer>
						<v-btn icon small @click="toggleMapView">
							<v-icon>{{ showFullMap ? 'unfold_less' : 'unfold_more' }}</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text>
						<v-layout row wrap>
							<v-flex xs12 sm6>
								<v-text-field
									label="Latitude"
									v-model.number="coordinates.lat"
									type="number"
									step="0.000001"
									required
									readonly
									@click="copyToClipboard('lat')"
									hint="Klik om te copieren"
								></v-text-field>
							</v-flex>
							<v-flex xs12 sm6>
								<v-text-field
									label="Longitude"
									v-model.number="coordinates.lng"
									type="number"
									step="0.000001"
									required
									readonly
									@click="copyToClipboard('lng')"
									hint="Klik om te copieren"
								></v-text-field>
							</v-flex>
							<v-flex xs12>
								<v-btn 
									color="success" 
									@click="applyCoordinates"
									small
									class="ml-2"
									:disabled="!coordinates.lat || !coordinates.lng"
								>
									<v-icon left>check</v-icon>
									Pas toe
								</v-btn>
							</v-flex>
						</v-layout>

						<v-divider class="my-3"></v-divider>

						<p class="caption grey--text">
							<v-icon small>info</v-icon>
							Klik op de kaart om coördinaten te selecteren
						</p>

						<div class="map-container" :style="{ height: showFullMap ? '600px' : '400px' }">
						<gmap-map
							:center="mapCenter"
							:zoom="mapZoom"
							style="width: 100%; height: 100%;"
							map-type-id="terrain"
							:options="mapOptions"
							@click="handleMapClick"
							@zoom_changed="onZoomChanged"
						>
							<!-- Current position marker -->
							<gmap-marker
								v-if="coordinates.lat && coordinates.lng"
								:position="{ lat: coordinates.lat, lng: coordinates.lng }"
								:icon="markerIcon"
								:draggable="true"
								@drag="handleMarkerDrag"
								@dragend="handleMarkerDragEnd"
							>
								<gmap-info-window :opened="infoWindowOpen">
									<div>
										<p><strong>Geselecteerde locatie</strong></p>
										<p>Lat: {{ coordinates.lat.toFixed(6) }}</p>
										<p>Lng: {{ coordinates.lng.toFixed(6) }}</p>
										<v-btn small color="primary" @click="applyCoordinates">
											Bevestigen
										</v-btn>
									</div>
								</gmap-info-window>
							</gmap-marker>
						</gmap-map>

						<div v-if="!mapInitialized" class="loading-overlay">
							<v-progress-circular indeterminate color="primary"></v-progress-circular>
						</div>
					</div>

					<v-alert v-if="!mapInitialized" type="warning">
						Map is aan het laden...
					</v-alert>
				</v-card-text>
				</v-card>
			</v-flex>
		</v-layout>
	</div>
</template>

<script>
export default {
	name: 'CoordinatePicker',
	props: {
		lat: {
			type: Number,
			default: 52.1326
		},
		lng: {
			type: Number,
			default: 5.2913
		}
	},
	data() {
		return {
			coordinates: {
				lat: this.lat || 52.1326,
				lng: this.lng || 5.2913
			},
			mapCenter: {
				lat: this.lat || 52.1326,
				lng: this.lng || 5.2913
			},
			mapZoom: 7,
			mapInitialized: false,
			showFullMap: false,
			infoWindowOpen: false,
			markerIcon: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
			mapOptions: {
				gestureHandling: 'greedy',
				styles: this.getDarkMapStyles()
			}
		};
	},
	mounted() {
		console.log('[CoordinatePicker] Component mounted');
		// Use nextTick to ensure Vue and Google Maps API are ready
		this.$nextTick(() => {
			if (typeof window.google !== 'undefined' && window.google.maps) {
				console.log('[CoordinatePicker] Google Maps API available immediately');
				this.mapInitialized = true;
			} else {
				// Wait for Google Maps to load
				const checkInterval = setInterval(() => {
					if (typeof window.google !== 'undefined' && window.google.maps) {
						console.log('[CoordinatePicker] Google Maps API loaded');
						this.mapInitialized = true;
						clearInterval(checkInterval);
					}
				}, 100);
				
				// Timeout after 10 seconds
				setTimeout(() => {
					if (!this.mapInitialized) {
						console.error('[CoordinatePicker] Failed to load Google Maps API after 10 seconds');
						clearInterval(checkInterval);
						this.mapInitialized = true; // Force show the map anyway
					}
				}, 10000);
			}
		});
	},
	methods: {
		handleMapClick(event) {
			console.log('[CoordinatePicker] Map clicked:', event.latLng);
			const lat = event.latLng.lat();
			const lng = event.latLng.lng();
			this.coordinates.lat = lat;
			this.coordinates.lng = lng;
			this.mapCenter = { lat, lng };
			this.infoWindowOpen = true;
		},
		handleMarkerDrag(event) {
			this.coordinates.lat = event.latLng.lat();
			this.coordinates.lng = event.latLng.lng();
		},
		handleMarkerDragEnd(event) {
			this.coordinates.lat = event.latLng.lat();
			this.coordinates.lng = event.latLng.lng();
		},
		onZoomChanged(zoom) {
			this.mapZoom = zoom;
		},
		setDefaultCenter() {
			this.coordinates = {
				lat: 52.1326,
				lng: 5.2913
			};
			this.mapCenter = {
				lat: 52.1326,
				lng: 5.2913
			};
			this.mapZoom = 7;
		},
		toggleMapView() {
			this.showFullMap = !this.showFullMap;
		},
		applyCoordinates() {
			this.$emit('coordinates-selected', {
				lat: this.coordinates.lat,
				lng: this.coordinates.lng
			});
		},
		copyToClipboard(field) {
			const value = this.coordinates[field];
			navigator.clipboard.writeText(value.toString()).then(() => {
				this.$emit('show-message', {
					msg: `${field.toUpperCase()} gecopieerd: ${value}`,
					type: 'success'
				});
			});
		},
		getDarkMapStyles() {
			return [
				{ elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
				{ elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
				{ elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
				{
					featureType: 'administrative.locality',
					elementType: 'labels.text.fill',
					stylers: [{ color: '#d59563' }]
				},
				{
					featureType: 'poi',
					elementType: 'labels.text.fill',
					stylers: [{ color: '#d59563' }]
				},
				{
					featureType: 'poi.park',
					elementType: 'geometry',
					stylers: [{ color: '#263c3f' }]
				},
				{
					featureType: 'poi.park',
					elementType: 'labels.text.fill',
					stylers: [{ color: '#6b9a76' }]
				},
				{
					featureType: 'road',
					elementType: 'geometry',
					stylers: [{ color: '#38414e' }]
				},
				{
					featureType: 'road',
					elementType: 'geometry.stroke',
					stylers: [{ color: '#212a37' }]
				},
				{
					featureType: 'road',
					elementType: 'labels.text.fill',
					stylers: [{ color: '#9ca5b3' }]
				},
				{
					featureType: 'road.highway',
					elementType: 'geometry',
					stylers: [{ color: '#746855' }]
				},
				{
					featureType: 'road.highway',
					elementType: 'geometry.stroke',
					stylers: [{ color: '#1f2835' }]
				},
				{
					featureType: 'road.highway',
					elementType: 'labels.text.fill',
					stylers: [{ color: '#f3d19c' }]
				},
				{
					featureType: 'transit',
					elementType: 'geometry',
					stylers: [{ color: '#2f3948' }]
				},
				{
					featureType: 'transit.station',
					elementType: 'labels.text.fill',
					stylers: [{ color: '#d59563' }]
				},
				{
					featureType: 'water',
					elementType: 'geometry',
					stylers: [{ color: '#17263c' }]
				},
				{
					featureType: 'water',
					elementType: 'labels.text.fill',
					stylers: [{ color: '#515c6d' }]
				},
				{
					featureType: 'water',
					elementType: 'labels.text.stroke',
					stylers: [{ color: '#17263c' }]
				}
			];
		}
	}
};
</script>

<style scoped>
.coordinate-picker {
	width: 100%;
}

.map-container {
	width: 100%;
	border: 1px solid #ddd;
	border-radius: 2px;
	margin-top: 16px;
	position: relative;
	overflow: hidden;
	background-color: #e5e3df;
	display: flex;
	align-items: center;
	justify-content: center;
}

.loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(255, 255, 255, 0.7);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 100;
}

.map-container >>> .gmap-map {
	width: 100% !important;
	height: 100% !important;
}
</style>



// WEBPACK FOOTER //
// src/components/subcomponents/CoordinatePicker.vue